import { useState } from "react";
import { Loader2, FileText } from "lucide-react";
import { subDays, format } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { invokeAuthenticatedFunction } from "@/lib/invoke-function";
import {
  generateProgressReportPDF,
  createPDFDownload,
} from "@/lib/export/progress-report";
import type { ProgressReportData } from "@/lib/export/progress-report";
import { useToast } from "@/hooks/use-toast";

interface ClientProgressReportProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
}

export function ClientProgressReport({
  open,
  onOpenChange,
  clientId,
  clientName,
}: ClientProgressReportProps) {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(false);
  const [trainerNotes, setTrainerNotes] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });

  async function handleGenerate() {
    if (!dateRange?.from || !dateRange?.to) {
      toast({
        title: "Date range required",
        description: "Please select a start and end date.",
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);
    try {
      const { data, error } = await invokeAuthenticatedFunction<ProgressReportData>(
        "generate-progress-report",
        {
          body: {
            client_id: clientId,
            date_from: format(dateRange.from, "yyyy-MM-dd"),
            date_to: format(dateRange.to, "yyyy-MM-dd"),
          },
        },
      );

      if (error || !data) {
        throw new Error(error || "Failed to generate report data");
      }

      const blob = await generateProgressReportPDF(data, trainerNotes || undefined);
      const safeClientName = clientName.replace(/[^a-zA-Z0-9]/g, "_");
      const dateLabel = format(dateRange.from, "yyyyMMdd") + "-" + format(dateRange.to, "yyyyMMdd");
      createPDFDownload(blob, `progress_report_${safeClientName}_${dateLabel}.pdf`);

      toast({
        title: "Report generated",
        description: `Progress report for ${clientName} has been downloaded.`,
      });

      onOpenChange(false);
    } catch (err: any) {
      console.error("Progress report generation failed:", err);
      toast({
        title: "Report generation failed",
        description: err.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Progress Report
          </DialogTitle>
          <DialogDescription>
            Generate a PDF progress report for {clientName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label>Date Range</Label>
            <DatePickerWithRange date={dateRange} onDateChange={setDateRange} />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="trainer-notes">
              Trainer Notes <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Textarea
              id="trainer-notes"
              placeholder="Add any personalized notes, observations, or recommendations..."
              value={trainerNotes}
              onChange={(e) => setTrainerNotes(e.target.value)}
              rows={4}
            />
          </div>

          <Button className="w-full" onClick={handleGenerate} disabled={generating}>
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-1.5" />
                Generate PDF
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
